import React, { useState, useEffect } from 'react'
import axios from 'axios'
import toast from 'react-hot-toast'
import { useAuthStore } from '../../stores/authStore'
import { usePermissions } from '../../hooks/usePermissions'
import PageHelp from '../../components/PageHelp'

interface ExchangeRate {
  id: string
  fromCurrency: string
  toCurrency: string
  rate: string
  effectiveDate: string
  source?: string
  notes?: string
  createdAt: string
  creator?: {
    firstName: string
    lastName: string
  }
}

interface Currency {
  code: string
  name: string
  symbol: string
}

const ExchangeRates: React.FC = () => {
  const { user } = useAuthStore()
  const { hasPermission, loading: permissionsLoading } = usePermissions(user)
  const [exchangeRates, setExchangeRates] = useState<ExchangeRate[]>([])
  const [currencies, setCurrencies] = useState<Currency[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showFetchModal, setShowFetchModal] = useState(false)
  const [selectedRate, setSelectedRate] = useState<ExchangeRate | null>(null)
  const [isFetching, setIsFetching] = useState(false)
  const [fetchData, setFetchData] = useState({
    date: '',
    baseCurrency: 'GBP',
    fetchType: 'latest' as 'latest' | 'date' | 'range',
    startDate: '',
    endDate: '',
  })
  const [formData, setFormData] = useState({
    fromCurrency: 'GBP',
    toCurrency: 'USD',
    rate: '',
    effectiveDate: new Date().toISOString().split('T')[0],
    source: 'MANUAL',
    notes: '',
  })
  const [filters, setFilters] = useState({
    fromCurrency: '',
    toCurrency: '',
    startDate: '',
    endDate: '',
  })

  useEffect(() => {
    fetchExchangeRates()
    fetchCurrencies()
  }, [])

  useEffect(() => {
    fetchExchangeRates()
  }, [filters])

  const fetchExchangeRates = async () => {
    try {
      setIsLoading(true)
      const params: any = {}
      if (filters.fromCurrency) params.fromCurrency = filters.fromCurrency
      if (filters.toCurrency) params.toCurrency = filters.toCurrency
      if (filters.startDate) params.startDate = filters.startDate
      if (filters.endDate) params.endDate = filters.endDate

      const response = await axios.get('/api/erp/exchange-rates', { params })
      setExchangeRates(response.data.data || [])
    } catch (error: any) {
      console.error('Error fetching exchange rates:', error)
      toast.error('Failed to load exchange rates')
    } finally {
      setIsLoading(false)
    }
  }

  const fetchCurrencies = async () => {
    try {
      const response = await axios.get('/api/erp/exchange-rates/currencies')
      setCurrencies(response.data.data || [])
    } catch (error) {
      console.error('Error fetching currencies:', error)
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
  }

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!formData.fromCurrency || !formData.toCurrency || !formData.rate || !formData.effectiveDate) {
      toast.error('Please fill in all required fields')
      return
    }

    if (formData.fromCurrency === formData.toCurrency) {
      toast.error('From currency and to currency must be different')
      return
    }

    const rate = parseFloat(formData.rate)
    if (isNaN(rate) || rate <= 0) {
      toast.error('Exchange rate must be a positive number')
      return
    }

    try {
      await axios.post('/api/erp/exchange-rates', formData)
      toast.success('Exchange rate created successfully!')
      setShowCreateModal(false)
      setFormData({
        fromCurrency: 'GBP',
        toCurrency: 'USD',
        rate: '',
        effectiveDate: new Date().toISOString().split('T')[0],
        source: 'MANUAL',
        notes: '',
      })
      fetchExchangeRates()
    } catch (error: any) {
      console.error('Error creating exchange rate:', error)
      toast.error(error.response?.data?.error || 'Failed to create exchange rate')
    }
  }

  const handleEdit = async (rate: ExchangeRate) => {
    setSelectedRate(rate)
    setFormData({
      fromCurrency: rate.fromCurrency,
      toCurrency: rate.toCurrency,
      rate: rate.rate,
      effectiveDate: rate.effectiveDate.split('T')[0],
      source: rate.source || 'MANUAL',
      notes: rate.notes || '',
    })
    setShowEditModal(true)
  }

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!selectedRate) return

    if (!formData.rate || !formData.effectiveDate) {
      toast.error('Please fill in all required fields')
      return
    }

    const rate = parseFloat(formData.rate)
    if (isNaN(rate) || rate <= 0) {
      toast.error('Exchange rate must be a positive number')
      return
    }

    try {
      await axios.put(`/api/erp/exchange-rates/${selectedRate.id}`, {
        rate: formData.rate,
        effectiveDate: formData.effectiveDate,
        source: formData.source,
        notes: formData.notes,
      })
      toast.success('Exchange rate updated successfully!')
      setShowEditModal(false)
      setSelectedRate(null)
      fetchExchangeRates()
    } catch (error: any) {
      console.error('Error updating exchange rate:', error)
      toast.error(error.response?.data?.error || 'Failed to update exchange rate')
    }
  }

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this exchange rate?')) return
    try {
      await axios.delete(`/api/erp/exchange-rates/${id}`)
      toast.success('Exchange rate deleted successfully!')
      fetchExchangeRates()
    } catch (error: any) {
      console.error('Error deleting exchange rate:', error)
      toast.error(error.response?.data?.error || 'Failed to delete exchange rate')
    }
  }

  const handleFetchRates = async () => {
    setIsFetching(true)
    try {
      const payload: any = {
        baseCurrency: fetchData.baseCurrency,
      }

      if (fetchData.fetchType === 'date') {
        payload.date = fetchData.date
      } else if (fetchData.fetchType === 'range') {
        payload.startDate = fetchData.startDate
        payload.endDate = fetchData.endDate
      }

      const response = await axios.post('/api/erp/exchange-rates/fetch', payload)
      const result = response.data.data

      toast.success(`Successfully fetched ${result.fetched} exchange rate(s)!`)
      
      if (result.errors && result.errors.length > 0) {
        console.warn('Some rates failed to fetch:', result.errors)
      }

      setShowFetchModal(false)
      setFetchData({
        date: '',
        baseCurrency: 'GBP',
        fetchType: 'latest',
        startDate: '',
        endDate: '',
      })
      fetchExchangeRates()
    } catch (error: any) {
      console.error('Error fetching exchange rates:', error)
      toast.error(error.response?.data?.error || 'Failed to fetch exchange rates')
    } finally {
      setIsFetching(false)
    }
  }

  if (permissionsLoading) {
    return <div className="p-6">Loading...</div>
  }

  if (!hasPermission('erp_exchange_rate', 'read')) {
    return (
      <div className="p-6">
        <p className="text-red-800">You don't have permission to view exchange rates.</p>
      </div>
    )
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold text-gray-900">Exchange Rates</h1>
          <PageHelp pageIdentifier="exchange-rates" title="Exchange Rates" />
        </div>
        {hasPermission('erp_exchange_rate', 'write') && (
          <button
            onClick={() => setShowCreateModal(true)}
            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
          >
            Add Exchange Rate
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">From Currency</label>
            <select
              value={filters.fromCurrency}
              onChange={(e) => setFilters({ ...filters, fromCurrency: e.target.value })}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            >
              <option value="">All</option>
              {currencies.map(currency => (
                <option key={currency.code} value={currency.code}>
                  {currency.code} - {currency.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">To Currency</label>
            <select
              value={filters.toCurrency}
              onChange={(e) => setFilters({ ...filters, toCurrency: e.target.value })}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            >
              <option value="">All</option>
              {currencies.map(currency => (
                <option key={currency.code} value={currency.code}>
                  {currency.code} - {currency.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
            <input
              type="date"
              value={filters.startDate}
              onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
            <input
              type="date"
              value={filters.endDate}
              onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            />
          </div>
        </div>
      </div>

      {isLoading ? (
        <p className="text-gray-600">Loading exchange rates...</p>
      ) : exchangeRates.length === 0 ? (
        <p className="text-gray-600">No exchange rates found. Add your first exchange rate.</p>
      ) : (
        <div className="bg-white shadow overflow-hidden sm:rounded-lg">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">From</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">To</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Rate</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Effective Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Source</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {exchangeRates.map((rate) => (
                <tr key={rate.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{rate.fromCurrency}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{rate.toCurrency}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">
                    {parseFloat(rate.rate).toLocaleString('en-GB', { minimumFractionDigits: 6, maximumFractionDigits: 6 })}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(rate.effectiveDate).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{rate.source || 'MANUAL'}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    {hasPermission('erp_exchange_rate', 'write') && (
                      <>
                        <button
                          onClick={() => handleEdit(rate)}
                          className="text-indigo-600 hover:text-indigo-900 mr-3"
                        >
                          Edit
                        </button>
                      </>
                    )}
                    {hasPermission('erp_exchange_rate', 'delete') && (
                      <button
                        onClick={() => handleDelete(rate.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        Delete
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75 overflow-y-auto h-full w-full z-50 flex justify-center items-center">
          <div className="bg-white p-8 rounded-lg shadow-xl max-w-2xl w-full">
            <h2 className="text-2xl font-bold mb-6 text-gray-900">Add Exchange Rate</h2>
            <form onSubmit={handleCreate}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    From Currency <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="fromCurrency"
                    value={formData.fromCurrency}
                    onChange={handleInputChange}
                    required
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  >
                    {currencies.map(currency => (
                      <option key={currency.code} value={currency.code}>
                        {currency.code} - {currency.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    To Currency <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="toCurrency"
                    value={formData.toCurrency}
                    onChange={handleInputChange}
                    required
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  >
                    {currencies.map(currency => (
                      <option key={currency.code} value={currency.code}>
                        {currency.code} - {currency.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Exchange Rate <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    step="0.000001"
                    min="0.000001"
                    name="rate"
                    value={formData.rate}
                    onChange={handleInputChange}
                    required
                    placeholder="e.g., 1.25"
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    1 {formData.fromCurrency} = {formData.rate || '?'} {formData.toCurrency}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Effective Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    name="effectiveDate"
                    value={formData.effectiveDate}
                    onChange={handleInputChange}
                    required
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Source</label>
                  <select
                    name="source"
                    value={formData.source}
                    onChange={handleInputChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  >
                    <option value="MANUAL">Manual Entry</option>
                    <option value="BANK">Bank</option>
                    <option value="API">API</option>
                  </select>
                </div>
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700">Notes</label>
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                  rows={3}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                ></textarea>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="bg-gray-300 text-gray-800 px-4 py-2 rounded-md hover:bg-gray-400"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
                >
                  Add Exchange Rate
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && selectedRate && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75 overflow-y-auto h-full w-full z-50 flex justify-center items-center">
          <div className="bg-white p-8 rounded-lg shadow-xl max-w-2xl w-full">
            <h2 className="text-2xl font-bold mb-6 text-gray-900">Edit Exchange Rate</h2>
            <form onSubmit={handleUpdate}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">From Currency</label>
                  <input
                    type="text"
                    value={formData.fromCurrency}
                    disabled
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 bg-gray-100 sm:text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">To Currency</label>
                  <input
                    type="text"
                    value={formData.toCurrency}
                    disabled
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 bg-gray-100 sm:text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Exchange Rate <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    step="0.000001"
                    min="0.000001"
                    name="rate"
                    value={formData.rate}
                    onChange={handleInputChange}
                    required
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    1 {formData.fromCurrency} = {formData.rate || '?'} {formData.toCurrency}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Effective Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    name="effectiveDate"
                    value={formData.effectiveDate}
                    onChange={handleInputChange}
                    required
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Source</label>
                  <select
                    name="source"
                    value={formData.source}
                    onChange={handleInputChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  >
                    <option value="MANUAL">Manual Entry</option>
                    <option value="BANK">Bank</option>
                    <option value="API">API</option>
                  </select>
                </div>
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700">Notes</label>
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                  rows={3}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                ></textarea>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false)
                    setSelectedRate(null)
                  }}
                  className="bg-gray-300 text-gray-800 px-4 py-2 rounded-md hover:bg-gray-400"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
                >
                  Update Exchange Rate
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Fetch Rates Modal */}
      {showFetchModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h2 className="text-xl font-bold mb-4">Fetch Exchange Rates from API</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Base Currency</label>
                <select
                  value={fetchData.baseCurrency}
                  onChange={(e) => setFetchData({ ...fetchData, baseCurrency: e.target.value })}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                >
                  {currencies.map(curr => (
                    <option key={curr.code} value={curr.code}>{curr.code} - {curr.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Fetch Type</label>
                <select
                  value={fetchData.fetchType}
                  onChange={(e) => setFetchData({ ...fetchData, fetchType: e.target.value as any })}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                >
                  <option value="latest">Latest Rates (Today)</option>
                  <option value="date">Specific Date</option>
                  <option value="range">Date Range</option>
                </select>
              </div>

              {fetchData.fetchType === 'date' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                  <input
                    type="date"
                    value={fetchData.date}
                    onChange={(e) => setFetchData({ ...fetchData, date: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                    required
                  />
                </div>
              )}

              {fetchData.fetchType === 'range' && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                    <input
                      type="date"
                      value={fetchData.startDate}
                      onChange={(e) => setFetchData({ ...fetchData, startDate: e.target.value })}
                      className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                    <input
                      type="date"
                      value={fetchData.endDate}
                      onChange={(e) => setFetchData({ ...fetchData, endDate: e.target.value })}
                      className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                      required
                    />
                  </div>
                </div>
              )}

              <div className="bg-blue-50 border border-blue-200 rounded p-3 text-sm text-blue-800">
                <p className="font-semibold mb-1">Note:</p>
                <p>Rates are fetched from exchangerate-api.com. Only rates that don't already exist will be created.</p>
              </div>
            </div>

            <div className="mt-6 flex justify-end space-x-2">
              <button
                type="button"
                onClick={() => {
                  setShowFetchModal(false)
                  setFetchData({
                    date: '',
                    baseCurrency: 'GBP',
                    fetchType: 'latest',
                    startDate: '',
                    endDate: '',
                  })
                }}
                className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
                disabled={isFetching}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleFetchRates}
                disabled={isFetching || (fetchData.fetchType === 'date' && !fetchData.date) || (fetchData.fetchType === 'range' && (!fetchData.startDate || !fetchData.endDate))}
                className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:bg-gray-400"
              >
                {isFetching ? 'Fetching...' : 'Fetch Rates'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ExchangeRates
